import * as ExcelJS from 'exceljs';

import { buildLengthMString } from './extract';
import { buildExportHeaderRows } from './header-rows';
import { ExportHeaderInfo, type CableTypeExportRow } from './types';

const TABLE_HEADERS = ['Type', 'Length (M)', 'Quantity'];

export const buildCableTypeExcelBlob = async (
  data: CableTypeExportRow[],
  filename: string,
  headerInfo?: ExportHeaderInfo,
): Promise<{ blob: Blob; filename: string }> => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Cable Type');

  let dataStartRow = 1;
  if (headerInfo) {
    const headerRows = buildExportHeaderRows(headerInfo, TABLE_HEADERS.length);
    headerRows.forEach((row) => sheet.addRow(row));
    const lastHeaderRow = headerRows.length;
    for (let r = 1; r <= lastHeaderRow; r += 1) {
      const row = sheet.getRow(r);
      row.eachCell((cell) => {
        cell.alignment = {
          wrapText: true,
          vertical: 'top',
          horizontal: 'left',
        };
      });
      row.getCell(1).font = { bold: true };
    }
    if (lastHeaderRow > 1) {
      sheet.mergeCells(1, 1, 1, TABLE_HEADERS.length);
    }
    sheet.getRow(1).getCell(1).font = { bold: true, size: 12 };
    dataStartRow = headerRows.length + 1;
  }

  sheet.addRow(TABLE_HEADERS);

  sheet.getRow(dataStartRow).eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDCE6F1' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  data.forEach((item) => {
    const rowValues = [
      item.Type,
      buildLengthMString(item['Length (M)'], 'excel'),
      item.Quantity,
    ];
    const row = sheet.addRow(rowValues);
    row.eachCell((cell, colNumber) => {
      cell.alignment = {
        vertical: 'middle',
        horizontal: colNumber === 1 ? 'left' : 'center',
        wrapText: true,
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
  });

  const MAX_HEADER_COL_WIDTH = 70;
  sheet.columns?.forEach((col, idx) => {
    let maxLength = 0;
    col.eachCell?.({ includeEmpty: true }, (cell: any) => {
      const cellValue = cell?.value ? cell.value.toString() : '';
      maxLength = Math.max(maxLength, cellValue.length);
    });
    const w = maxLength + 2;
    col.width = idx < 2 ? Math.min(w, MAX_HEADER_COL_WIDTH) : w;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return { blob, filename: `${filename}.xlsx` };
};
